import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/Navbar";

const TOPICS = [
  {
    n: "01",
    title: "From Loss to Light",
    desc: "Transforming grief into purpose — a roadmap for moving through devastating loss without losing yourself.",
  },
  {
    n: "02",
    title: "After Suicide Loss",
    desc: "Changing the conversation around suicide grief with compassion, candor, and hard-won hope.",
  },
  {
    n: "03",
    title: "The Resilient Spirit",
    desc: "Post-traumatic growth and the science-meets-soul path to becoming stronger after tragedy.",
  },
  {
    n: "04",
    title: "Healing the Addicted Heart",
    desc: "Where grief and addiction meet — and how families and individuals find their way back.",
  },
  {
    n: "05",
    title: "Dying to Live",
    desc: "What a near-death experience at four-and-a-half taught her about fear, purpose, and being fully alive.",
  },
  {
    n: "06",
    title: "Infinite Wisdom",
    desc: "Intuition, mediumship, and the lessons the spirit world holds for modern life.",
  },
];

export default function Speaking() {
  return (
    <section id="speaking" data-testid="speaking-section" className="relative bg-[#F7F1E6] py-28 text-[#6B5D4F] lg:py-40">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -left-8 top-6 select-none font-display text-[16rem] font-light italic leading-none text-[#C5A059]/10"
      >
        02
      </span>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
        >
          Chapter 02 — Signature Keynotes
        </motion.p>
        <div className="mt-6 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            className="font-display2 uppercase leading-[0.88] tracking-[0.01em] text-5xl sm:text-6xl lg:text-8xl"
          >
            Speaking that stays <br />
            <span className="font-display normal-case italic tracking-normal text-[#C5A059]">in the room.</span>
          </motion.h2>
          <motion.button
            data-testid="speaking-book-btn"
            onClick={() => scrollToId("#press")}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="group flex w-fit items-center gap-3 border border-[#6B5D4F]/30 px-7 py-3.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] transition-colors duration-500 hover:border-[#C5A059] hover:text-[#C5A059]"
          >
            Request Availability
            <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </motion.button>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-8 max-w-xl font-body text-sm font-light leading-relaxed text-[#6B5D4F]/55"
        >
          Susan's 2026 calendar is a run of sold-out symposiums, expos, and headline events —
          including headlining the New Visions Holistic Wellness Expo in Pennsylvania, a sold-out
          Spirit Encounter at the Omega Institute in Rhinebeck, New York, and featured lectures
          with Goop across the United States and Canada.
        </motion.p>

        <div className="mt-20">
          {TOPICS.map((t, i) => (
            <motion.div
              key={t.n}
              data-testid={`speaking-topic-${t.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: [0.76, 0, 0.24, 1] }}
              className="group grid cursor-default grid-cols-12 items-baseline gap-4 border-t border-[#6B5D4F]/10 py-8 transition-colors duration-500 last:border-b hover:bg-[#6B5D4F]/[0.03] md:py-10"
            >
              <span className="col-span-2 font-display text-xl italic text-[#C5A059] md:col-span-1 md:text-2xl">{t.n}</span>
              <h3 className="col-span-10 font-display text-3xl font-light tracking-tight transition-transform duration-500 group-hover:translate-x-3 md:col-span-4 md:text-4xl">
                {t.title}
              </h3>
              <p className="col-span-10 col-start-3 font-body text-sm font-light leading-relaxed text-[#6B5D4F]/60 md:col-span-6 md:col-start-6 md:text-base">
                {t.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
